import { css } from "@emotion/react";
import { isEmpty, noop } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { Field, Form, FormSpy } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";

import { PopperTooltip } from "@livechat/design-system";
import { Button, DesignToken, FormField, RadiusToken } from "@livechat/design-system-react-components";

import { InputFieldFullWidthFinalForm } from "components/design-system-adapters/InputField";
import { TextAreaAutosizeFieldFinalForm } from "components/design-system-adapters/TextAreaFieldFinalForm";
import { CustomFieldTypes } from "constants/custom-fields";
import { DateFormat } from "constants/date";
import { TicketSilo } from "interfaces/entities/ticket";
import { selectIsAdmin, selectIsViewer } from "store/entities/agents";
import { ICustomField } from "store/entities/custom-fields/interfaces";
import { TicketActions } from "store/entities/tickets/actions";
import { selectCanManageTickets } from "store/views/tickets/selectors";
import { StackingContextLevel } from "styles/stacking-context-level";
import { destringifyKeys } from "utils/form";
import { compose, dateFormat, maxLength, url } from "utils/validators";

import TicketInfoCustomFieldDate from "./TicketInfoCustomFieldDate";
import TicketInfoCustomFieldLink from "./TicketInfoCustomFieldLink";
import {
  customFieldButtonStyles,
  customFieldFormFieldStyles,
  customFieldInputStyles,
} from "./TicketInfoCustomFieldStyles";

type ITicketInfoCustomFieldProps = Partial<ICustomField> & {
  ticketId: string;
  ticketSilo: TicketSilo;
  value?: string;
  isMemberOfTicketTeam?: boolean;
  isChildTicket?: boolean;
};

const customFieldTextareaStyles = css`
  textarea {
    border: none;
    padding: 8px 0;
    resize: none;
    background-color: transparent;
    color: var(${DesignToken.ContentBasicPrimary});
    border-radius: var(${RadiusToken.Radius3});
    transition: padding 0.2s ease-in-out;
    width: 94%;
    max-width: 100%;
  }

  textarea::placeholder {
    color: var(${DesignToken.ContentBasicDisabled});
    opacity: 1;
  }

  textarea:hover {
    border: none;
  }

  textarea:focus {
    width: 100%;
    padding: 8px 8px;
    box-shadow: 0 0 0 1px var(${DesignToken.ActionPrimaryDefault});
  }
`;

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function renderInputComponent(type: CustomFieldTypes) {
  if (type === CustomFieldTypes.MultiLine) return TextAreaAutosizeFieldFinalForm;
  if (type === CustomFieldTypes.Date) return TicketInfoCustomFieldDate;
  if (type === CustomFieldTypes.Url) return TicketInfoCustomFieldLink;
  return InputFieldFullWidthFinalForm;
}

export const TicketInfoCustomField = ({
  displayName,
  value,
  type,
  ticketId,
  ticketSilo,
  apiKey,
  roleLevel,
  isMemberOfTicketTeam,
  isChildTicket,
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
}: ITicketInfoCustomFieldProps) => {
  const [isInputFocused, setIsInputFocused] = useState<boolean>(false);
  const isAdmin = useSelector(selectIsAdmin);
  const isViewer = useSelector(selectIsViewer);
  const canManageTickets = useSelector(selectCanManageTickets);
  const inputRef = useRef(null);
  const readOnly = roleLevel === "readOnly";
  const adminOnly = roleLevel === "owner" && !isAdmin;
  const isRecentTicket = ticketSilo === TicketSilo.Tickets;
  const showTooltip = (adminOnly || readOnly) && !isViewer;
  const disabled =
    adminOnly || readOnly || !canManageTickets || !isMemberOfTicketTeam || isChildTicket || !isRecentTicket;
  const dispatch = useDispatch();
  let resetForm: (initialValues?: Record<string, string>) => void;
  let timeOut: ReturnType<typeof setTimeout>;

  const handleEscPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Escape") {
        resetForm();
        // @ts-expect-error TS(2339): Property 'blur' does not exist on type 'never'.
        inputRef.current?.blur();
      }
    },
    // @ts-expect-error TS(2454): Variable 'resetForm' is used before being assigned... Remove this comment to see the full error message
    [resetForm]
  );

  useEffect(() => {
    const { current } = inputRef;
    // @ts-expect-error TS(2339): Property 'addEventListener' does not exist on type... Remove this comment to see the full error message
    current?.addEventListener("keyup", handleEscPress);
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => {
      clearTimeout(timeOut);
      // @ts-expect-error TS(2339): Property 'removeEventListener' does not exist on t... Remove this comment to see the full error message
      current?.removeEventListener("keyup", handleEscPress);
    };
    // @ts-expect-error TS(2454): Variable 'timeOut' is used before being assigned.
  }, [handleEscPress, timeOut]);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  const handleSubmitForm = (data: Record<string, string>, form: any) => {
    const { pristine, valid } = form.getState();
    if (!pristine && valid) {
      dispatch(
        TicketActions.updateCustomField({
          ticketId,
          customFields: destringifyKeys(data),
        })
      );
    }
    // @ts-expect-error TS(2339): Property 'blur' does not exist on type 'never'.
    inputRef.current?.blur();
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleInputFocus = () => {
    setIsInputFocused(true);
    // @ts-expect-error TS(2339): Property 'focus' does not exist on type 'never'.
    timeOut = setTimeout(() => inputRef.current?.focus(), 0);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const formButtonText = (isActive: boolean) => {
    if (isActive) {
      return "Save";
    }
    return "Edit";
  };

  return (
    // @ts-expect-error TS(2464)
    <Form onSubmit={handleSubmitForm} initialValues={{ [apiKey]: value }}>
      {/* TODO: Fix this the next time the file is edited. */}
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {({ handleSubmit, form }: any) => {
        resetForm = form.reset;
        return (
          <form id={`ticket-details-${displayName}-form`} onSubmit={handleSubmit}>
            <FormField
              css={customFieldFormFieldStyles}
              labelRightNode={
                <div css={isInputFocused && css({ cursor: "pointer" })}>
                  <Button
                    data-testid={`ticket-details-${displayName}-form-edit-button`}
                    kind="link-light"
                    disabled={disabled}
                    css={[isInputFocused && css({ pointerEvents: "none" }), customFieldButtonStyles]}
                    onClick={handleInputFocus}
                  >
                    {showTooltip ? (
                      <PopperTooltip
                        placement="left"
                        trigger={<span>Edit</span>}
                        css={css({ 
                          width: "300px",
                          whiteSpace: "normal",
                          wordWrap: "break-word"
                        })}
                        modifiers={{
                          offset: {
                            offset: "0 12px",
                          },
                        }}
                        zIndex={StackingContextLevel.Tooltip}
                      >
                        {readOnly ? (
                          <span>This custom field value can only be edited through the API.</span>
                        ) : (
                          <span>Only admins can edit this custom field value.</span>
                        )}
                      </PopperTooltip>
                    ) : (
                      formButtonText(isInputFocused)
                    )}
                  </Button>
                </div>
              }
              labelText={displayName}
            >
              <Field
                ref={inputRef}
                // @ts-expect-error TS(2322)
                name={apiKey}
                placeholder="No value provided"
                css={[customFieldInputStyles, customFieldTextareaStyles]}
                disabled={disabled}
                isInputFocused={isInputFocused}
                handleFormReset={resetForm}
                validate={compose(
                  type === CustomFieldTypes.SingleLine ? maxLength("Enter a max. of 28 characters", 28) : noop,
                  type === CustomFieldTypes.MultiLine ? maxLength("Enter a max. of 480 characters", 480) : noop,
                  type === CustomFieldTypes.Url ? url("Use the following URL link format: https://website.com") : noop,
                  type === CustomFieldTypes.Date
                    ? dateFormat(`Use the correct ${DateFormat.HtmlInputDate} date format`)
                    : noop
                )}
                id={`ticket-details-${displayName}`}
                component={renderInputComponent(type as CustomFieldTypes)}
                data-testid={`custom-field-${type}-type`}
              />
            </FormField>
            <FormSpy
              // FIXME: Fix this the next time the file is edited.
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onChange={({ values, valid, active, pristine }: any) => {
                setIsInputFocused(Boolean(active));
                if (valid && !active && !pristine) {
                  dispatch(
                    TicketActions.updateCustomField({
                      ticketId,
                      // @ts-expect-error TS(2464): A computed property name must be of type 'string',... Remove this comment to see the full error message
                      customFields: !isEmpty(values) ? destringifyKeys(values) : destringifyKeys({ [apiKey]: null }),
                    })
                  );
                }
              }}
            />
          </form>
        );
      }}
    </Form>
  );
};
